import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Radio, Space, Typography, message, Spin } from 'antd';
import api from '../../services/api';

const { Text, Paragraph } = Typography;

const RpEntityMergeModal = ({ open, entity, onClose, onMerged }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [npcs, setNpcs] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [targetType, setTargetType] = useState('npc');
  const [targetId, setTargetId] = useState(null);
  const [survivorChoice, setSurvivorChoice] = useState('current');

  useEffect(() => {
    if (!open || !entity?.universe_id) {
      return;
    }

    const fetchCandidates = async () => {
      setLoading(true);
      try {
        const response = await api.get('/api/admin/rp-entities/merge-candidates', {
          params: {
            type: entity.type,
            id: entity.id,
            universe_id: entity.universe_id,
          },
        });
        setNpcs(response.data?.npcs || []);
        setCharacters(response.data?.characters || []);
      } catch (error) {
        message.error(error.response?.data?.error || 'Erreur lors du chargement des candidats');
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
    setTargetType('npc');
    setTargetId(null);
    setSurvivorChoice('current');
  }, [open, entity]);

  const targetOptions = useMemo(() => {
    const source = targetType === 'npc' ? npcs : characters;
    return source.map((item) => ({
      value: item.id,
      label: item.label || item.name,
    }));
  }, [targetType, npcs, characters]);

  const selectedTarget = useMemo(() => {
    const source = targetType === 'npc' ? npcs : characters;
    return source.find((item) => item.id === targetId) || null;
  }, [targetType, targetId, npcs, characters]);

  const survivor = survivorChoice === 'current'
    ? { type: entity.type, id: entity.id, name: entity.name }
    : { type: targetType, id: selectedTarget?.id, name: selectedTarget?.name };

  const absorbed = survivorChoice === 'current'
    ? { type: targetType, id: selectedTarget?.id, name: selectedTarget?.name }
    : { type: entity.type, id: entity.id, name: entity.name };

  const handleMerge = async () => {
    if (!selectedTarget || !entity) {
      message.warning('Sélectionnez une entité à fusionner');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/api/admin/rp-entities/merge', {
        survivorType: survivor.type,
        survivorId: survivor.id,
        absorbedType: absorbed.type,
        absorbedId: absorbed.id,
      });
      message.success('Fusion effectuée');
      onMerged?.({
        survivorType: response.data?.survivorType,
        survivorId: response.data?.survivorId,
        result: response.data?.result,
      });
      onClose();
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur lors de la fusion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Fusionner des entités RP"
      open={open}
      onCancel={onClose}
      onOk={handleMerge}
      okText="Confirmer la fusion"
      okButtonProps={{ danger: true, disabled: !selectedTarget, loading: submitting }}
      cancelText="Annuler"
      width={700}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Paragraph type="secondary">
            L'entité conservée garde ses propriétés. Les threads et posts de l'entité absorbée lui sont réassignés, puis l'absorbée est supprimée (ou abandonnée).
          </Paragraph>

          <div>
            <Text strong>Type d'entité cible</Text>
            <Radio.Group
              style={{ display: 'block', marginTop: 8 }}
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value);
                setTargetId(null);
              }}
            >
              <Radio value="npc">PNJ</Radio>
              <Radio value="character">Personnage</Radio>
            </Radio.Group>
          </div>

          <div>
            <Text strong>Entité à fusionner avec</Text>
            <Select
              showSearch
              placeholder="Choisir dans le même univers"
              style={{ width: '100%', marginTop: 8 }}
              value={targetId}
              onChange={setTargetId}
              options={targetOptions}
              optionFilterProp="label"
            />
          </div>

          {selectedTarget && (
            <div>
              <Text strong>Entité à conserver</Text>
              <Radio.Group
                style={{ display: 'block', marginTop: 8 }}
                value={survivorChoice}
                onChange={(e) => setSurvivorChoice(e.target.value)}
              >
                <Space direction="vertical">
                  <Radio value="current">
                    Conserver {entity.name} (actuel) — absorber {selectedTarget.name}
                  </Radio>
                  <Radio value="other">
                    Conserver {selectedTarget.name} — absorber {entity.name} (actuel)
                  </Radio>
                </Space>
              </Radio.Group>
            </div>
          )}

          {survivor?.name && absorbed?.name && (
            <Paragraph>
              <Text type="danger">
                Récapitulatif : « {survivor.name} » ({survivor.type}) sera conservé, « {absorbed.name} » ({absorbed.type}) sera absorbé.
              </Text>
            </Paragraph>
          )}
        </Space>
      )}
    </Modal>
  );
};

export default RpEntityMergeModal;
